import React, { useCallback, useMemo, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  JourneyDraftAddForm,
  JourneyDraftAddInitialValues,
  JourneyDraftComponents,
  JourneyDraftAdditionalParams,
  JourneyDraftFormOptions,
  onJourneyDraftFormSaved,
  IJourneyDraftFormOptionsProps,
  AlertError,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateJourneyDraft, PermissionEnum, } from 'atlas-shared';
import { createJourneyDraft, getJourneyDraftCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useJourneies, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateJourneyDraft> {}

const JourneyDraftFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const journeies = useJourneies();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const param_props: IJourneyDraftFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    journeies: journeies.journeies,
  };
  const formLayout = useMemo(() => JourneyDraftAddForm(values, param_props, t), [values, organizations, journeies, t, abilities, ]);
  const options: IFormOptions = useMemo(() => JourneyDraftFormOptions(values || JourneyDraftAddInitialValues, param_props, t), [values, organizations, journeies, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateJourneyDraft, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateJourneyDraft) => save(() => createJourneyDraft(values).then(
    res => onSaved ? onSaved(res, navigate) : onJourneyDraftFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !journeies.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.Journey, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={JourneyDraftComponents}
        additionalParams={JourneyDraftAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...JourneyDraftAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('JOURNEY_DRAFT')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const JourneyDraftFormAddView = React.memo(withAdd(JourneyDraftFormAdd, getJourneyDraftCreateSchema, JourneyDraftAddInitialValues));
